import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import { MOVING, networkCheckQuery, networkChecksQuery, runNetworkCheck, type NetworkCheck, type NetworkCheckState } from '../../api/securiteeNetworkChecks'
import { Modal } from '../../ui/Modal'
import { Content } from '../../ui/Shell'
import { Spinner } from '../../ui/Spinner'
import './network-checks.css'

/* Cat se reciteste ecranul cat o verificare asteapta sau scaneaza. */
const REFETCH_MS = 10_000

const STATE: Record<NetworkCheckState, [string, string]> = {
  waiting: ['Waiting', 'label-info'],
  starting: ['Starting', 'label-info'],
  running: ['Scanning', 'label-primary'],
  done: ['Done', 'label-success'],
  busy: ['Agent busy', 'label-warning'],
  failed: ['Failed', 'label-danger'],
  done_no_run: ['No scan', 'label-default'],
  unknown: ['Unknown', 'label-default'],
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`
const took = (c: NetworkCheck) => (c.minutes !== null ? ` Took ${c.minutes} min.` : '')

/**
 * Securitee > Network checks: verificarea pornita de om (nu automata) pe
 * agentii de descoperire ai firmei - Telnet, FTP, VNC, AJP, SNMP "public".
 * Agentul scaneaza razele lui (EXD); ecranul arata ce a vazut rularea.
 */
export function NetworkChecksPage() {
  const client = useQueryClient()
  const page = useQuery({ ...networkChecksQuery(), refetchInterval: (q) => (q.state.data?.checks.some((c) => MOVING.includes(c.state)) ? REFETCH_MS : false) })
  /* implicit toti agentii; se tin minte cei scosi */
  const [left, setLeft] = useState<number[]>([])
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')
  const [open, setOpen] = useState<number | null>(null)
  const data = page.data

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <Content>
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
        </div>
      </Content>
    )
  }
  if (!data) return <Spinner />

  const picked = data.relays.filter((r) => !left.includes(r.id))
  const run = async () => {
    setBusy(true)
    try {
      client.setQueryData(networkChecksQuery().queryKey, await runNetworkCheck(picked.map((r) => r.id)))
      setNote(`Asked ${plural(picked.length, 'agent')} to scan now. The result shows below when the scan ends.`)
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'The check could not be started.')
    } finally {
      setBusy(false)
      setConfirming(false)
    }
  }

  return (
    <Content>
      <div id="content" className="nc-page">
        <header className="panel-heading">
          <h3>
            <strong>Network checks</strong>
          </h3>
          <label className="color">Insecure services seen by a scan you start · nothing runs on its own</label>
        </header>

        <section className="nc-run">
          <p className="nc-what">
            The discovery agents scan their ranges now: open TCP ports and one SNMP question with the community &quot;public&quot;. The check reports Telnet, FTP, VNC,
            AJP and SNMP &quot;public&quot;. It logs in nowhere and changes nothing. Run it only when the client agrees to a scan of their network.
          </p>
          {data.relays.length === 0 ? (
            <p className="nc-empty">No discovery agent of this company has an active range. Add ranges in Scan scope first.</p>
          ) : !data.can_run ? (
            <p className="nc-muted">Starting a check needs the right to run discovery scans.</p>
          ) : (
            <>
              <fieldset className="nc-relays">
                <legend className="sr-only">Discovery agents</legend>
                {data.relays.map((r) => (
                  <label key={r.id} className="nc-relay">
                    <input
                      type="checkbox"
                      checked={!left.includes(r.id)}
                      disabled={confirming}
                      onChange={(e) => setLeft((l) => (e.target.checked ? l.filter((x) => x !== r.id) : [...l, r.id]))}
                    />{' '}
                    {r.name} <span className="nc-muted">· {plural(r.ranges, 'range')}</span>
                  </label>
                ))}
              </fieldset>
              {confirming ? (
                <div className="nc-confirm" role="group" aria-label="Confirm the scan">
                  <span>
                    Scan the ranges of {plural(picked.length, 'agent')} now? Every address in them gets a few packets.
                  </span>
                  <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void run()}>
                    Yes, scan now
                  </button>
                  <button type="button" className="btn btn-default btn-sm" disabled={busy} onClick={() => setConfirming(false)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <button type="button" className="btn btn-primary btn-sm" disabled={picked.length === 0} onClick={() => setConfirming(true)}>
                  Run check
                </button>
              )}
              {note && <p className="nc-note">{note}</p>}
            </>
          )}
        </section>

        {data.checks.length === 0 ? (
          <p className="nc-empty">No checks yet.</p>
        ) : (
          <div className="nc-table-wrap">
            <table className="table nc-table">
              <thead>
                <tr>
                  <th scope="col">Requested</th>
                  <th scope="col">Agent</th>
                  <th scope="col">State</th>
                  <th scope="col">Findings</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.checks.map((c) => (
                  <tr key={c.id} id={`nc-row-${c.id}`}>
                    <td className="nc-when">{c.requested_at.slice(0, 16)} UTC</td>
                    <td>{c.relay.name || `device #${c.relay.id}`}</td>
                    <td>
                      <span className={`label ${STATE[c.state][1]}`}>{STATE[c.state][0]}</span>{' '}
                      <span className="nc-state-note">
                        {c.note}
                        {took(c)}
                      </span>
                    </td>
                    <td>
                      {c.findings == null ? '—' : c.findings === 0 ? <span className="nc-ok">None</span> : <span className="nc-bad">{plural(c.findings, 'finding')}</span>}
                    </td>
                    <td>
                      {(c.state === 'done' || c.state === 'running') && (
                        <button type="button" className="btn btn-default btn-xs" onClick={() => setOpen(c.id)}>
                          Details
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {open !== null && <CheckDialog id={open} onClose={() => setOpen(null)} />}
      </div>
    </Content>
  )
}

/** Aparatele cu ceva nesigur, din rularea verificarii. */
function CheckDialog({ id, onClose }: { id: number; onClose(): void }) {
  const q = useQuery({ ...networkCheckQuery(id), refetchInterval: (x) => (x.state.data && MOVING.includes(x.state.data.state) ? REFETCH_MS : false) })
  const d = q.data

  return (
    <Modal
      id="network-check"
      open
      large
      onClose={onClose}
      title={d ? `Check on ${d.relay.name || `device #${d.relay.id}`}` : 'Check'}
      footer={
        <button type="button" className="btn btn-default" onClick={onClose}>
          Close
        </button>
      }
    >
      {q.isError ? (
        <p className="text-danger">{(q.error as Error).message}</p>
      ) : !d ? (
        <Spinner />
      ) : (
        <>
          <p className="nc-muted">
            Requested {d.requested_at.slice(0, 16)} UTC · {d.note}
            {took(d)}
          </p>
          {d.devices.length === 0 ? (
            <p className="nc-ok">No insecure service on the {plural(d.scanned, 'device')} that answered.</p>
          ) : (
            <ul className="nc-devices">
              {d.devices.map((dev) => (
                <li key={dev.ip} id={`nc-device-${dev.ip}`}>
                  <strong>{dev.ip}</strong>
                  {dev.hostname && <span className="nc-muted"> · {dev.hostname}</span>}
                  <ul>
                    {dev.findings.map((f) => (
                      <li key={f.service} className="nc-finding">
                        <span className="label label-warning">
                          {f.title} · {f.port}
                        </span>{' '}
                        {f.why}
                        {f.evidence && <div className="nc-evidence">Seen: {f.evidence}</div>}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Modal>
  )
}
